import classNames from 'classnames'
import React, { forwardRef } from 'react'

import styles from './radioButtonV2.module.scss'

export interface RadioButtonV2Props {
  id: string
  name: string
  value: string
  label: string
  checked?: boolean
  disabled?: boolean
  className?: string
  labelClassName?: string
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
  'aria-describedby'?: string
}

export const RadioButtonV2 = forwardRef<HTMLInputElement, RadioButtonV2Props>(
  (
    {
      id,
      name,
      value,
      label,
      checked = false,
      disabled = false,
      className = '',
      labelClassName = '',
      onChange,
      'aria-describedby': ariaDescribedby,
    },
    ref,
  ) => {
    const wrapperClasses = classNames(styles['radio-button-v2'] as string, 'radio-button-v2', className, {
      [styles['radio-button-checked'] as string]: checked,
      [styles['radio-button-disabled'] as string]: disabled,
    })

    const inputClasses = classNames(styles['radio-input'] as string, {
      [styles['radio-input-checked'] as string]: checked,
      [styles['radio-input-disabled'] as string]: disabled,
    })

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
      if (onChange && !disabled) {
        e.preventDefault()
        e.stopPropagation()
        const input = e.currentTarget.querySelector('input')
        if (input) {
          const inputEvent = {
            target: input,
          } as React.ChangeEvent<HTMLInputElement>
          onChange(inputEvent)
        }
      }
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if ((e.key === 'Enter' || e.key === ' ') && onChange && !disabled) {
        e.preventDefault()
        const input = e.currentTarget.querySelector('input')
        if (input) {
          const inputEvent = {
            target: input,
          } as React.ChangeEvent<HTMLInputElement>
          onChange(inputEvent)
        }
      }
    }

    return (
      <div
        className={wrapperClasses}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
        aria-checked={checked}
        aria-disabled={disabled}
      >
        <input
          ref={ref}
          type="radio"
          id={id}
          name={name}
          value={value}
          checked={checked}
          disabled={disabled}
          onChange={onChange}
          className={inputClasses}
          aria-checked={checked}
          aria-describedby={ariaDescribedby}
          onClick={e => {
            e.stopPropagation()
          }}
        />
        <label htmlFor={id} className={classNames(styles['radio-label'], labelClassName)}>
          {label}
        </label>
      </div>
    )
  },
)

// Add display name for better debugging
RadioButtonV2.displayName = 'RadioButtonV2'
